import type { TherapySession } from './attendance.js';

/** 사회서비스 전자바우처 포털 승인내역 1행(파싱 후 정규화된 형태). */
export interface VoucherApproval {
  approvalNo: string;
  clientId: string;      // 센터 이용자와 매칭된 id
  therapistId?: string;  // 제공인력 매칭(없을 수 있음)
  approvedAt: string;    // ISO, 단말기 승인 일시
  amount: number;
  program: string;
}

export const FRAUD_RULES = {
  NO_SESSION_ON_DATE: { score: 60, label: '승인일에 해당 이용자의 완료된 회기 없음' },
  OUTSIDE_WINDOW: { score: 30, label: '승인 시각이 회기 시간대(전후 허용범위) 밖' },
  THERAPIST_OVERLAP: { score: 50, label: '같은 제공인력이 같은 시간에 다른 이용자 승인' },
  BULK_APPROVAL: { score: 40, label: '짧은 시간에 같은 이용자 승인 여러 건(일괄결제 의심)' },
  EDITED_AFTER_PAYMENT: { score: 40, label: '결제 후 출결이 수정된 회기' },
} as const;
export type FraudRule = keyof typeof FRAUD_RULES;

export interface FraudFinding {
  approvalNo: string;
  clientId: string;
  rules: FraudRule[];
  score: number; // 0~100, 높을수록 위험
  level: 'OK' | 'REVIEW' | 'HIGH';
}

export interface FraudOptions {
  windowMinutes?: number;     // 회기 시작 전/종료 후 허용(기본 60분)
  bulkWindowMinutes?: number; // 일괄결제 판단 간격(기본 10분)
}

const kstDate = (iso: string) => new Date(new Date(iso).getTime() + 9 * 3600_000).toISOString().slice(0, 10);
const ms = (iso: string) => new Date(iso).getTime();

/**
 * 바우처 승인내역 ↔ 회기(출결) 대조. 규칙별 점수를 합산(최대 100)해 위험도를 매긴다.
 * 판정은 '의심'일 뿐이며, 최종 판단은 사람이 소명 절차로 한다.
 */
export function detectVoucherFraud(
  approvals: readonly VoucherApproval[],
  sessions: readonly TherapySession[],
  opts: FraudOptions = {},
): FraudFinding[] {
  const windowMs = (opts.windowMinutes ?? 60) * 60_000;
  const bulkMs = (opts.bulkWindowMinutes ?? 10) * 60_000;
  const completed = sessions.filter((s) => s.status === 'COMPLETED');

  return approvals.map((a) => {
    const rules = new Set<FraudRule>();
    const t = ms(a.approvedAt);
    const sameDay = completed.filter((s) => s.clientId === a.clientId && kstDate(s.startAt) === kstDate(a.approvedAt));

    if (!sameDay.length) rules.add('NO_SESSION_ON_DATE');
    else {
      const inWindow = sameDay.filter((s) => t >= ms(s.startAt) - windowMs && t <= ms(s.endAt) + windowMs);
      if (!inWindow.length) rules.add('OUTSIDE_WINDOW');
      if (inWindow.some((s) => s.editedAfterPayment)) rules.add('EDITED_AFTER_PAYMENT');
    }

    if (a.therapistId) {
      const clash = approvals.some((b) => b !== a && b.therapistId === a.therapistId && b.clientId !== a.clientId &&
        Math.abs(ms(b.approvedAt) - t) < 30 * 60_000);
      if (clash) rules.add('THERAPIST_OVERLAP');
    }

    const burst = approvals.filter((b) => b.clientId === a.clientId && Math.abs(ms(b.approvedAt) - t) <= bulkMs);
    if (burst.length >= 2) rules.add('BULK_APPROVAL');

    const list = [...rules];
    const score = Math.min(100, list.reduce((n, r) => n + FRAUD_RULES[r].score, 0));
    return { approvalNo: a.approvalNo, clientId: a.clientId, rules: list, score, level: score >= 60 ? 'HIGH' : score > 0 ? 'REVIEW' : 'OK' };
  });
}
